import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL, AUTH_JWT, AUTH_PARTNER_INTEGRATION, SITE_V3 } from "@config/config";
import toSeoUrl from "@functions/toSeoUrl";
import { AuthContext } from "@context/AuthContext";

export const ApplicationContext = createContext({});

const ApplicationContextProvider = (props) => {
    const { siteVersion } = useContext(AuthContext);
    const [brandCode, setBrandCode] = useState("brand_choovie");
    const [brandName, setBrandName] = useState("");
    const [subscriberGroupId, setSubscriberGroupId] = useState(null);
    const [siteConfig, setSiteConfig] = useState({});
    const [publicSubscriberGroup, setPublicSubscriberGroup] = useState(null);
    const [showWelcomePopup, setShowWelcomePopup] = useState(true);
    const [cdnUrl, setCdnUrl] = useState(null);
    const [showCarousel, setShowCarousel] = useState(false);
    const [gaCode, setGaCode] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [subscriberGroupTerms, setSubscriberGroupTerms] = useState("Could not fetch Terms & Conditions.");
    const [subscriberGroup, setSubscriberGroup] = useState({});
    const [cinemas, setCinemas] = useState([]);
    const [templateTheme, setTemplateTheme] = useState(null);
    const [movies, setMovies] = useState([]);
    const [whiteLabel, setWhiteLabel] = useState(null);
    const [productTypeId, setProductTypeId] = useState(null);
    const [seasonalRoutes, setSeasonalRoutes] = useState(null);
    const [topBanner, setTopBanner] = useState(null);
    const [news, setNews] = useState({
        id: null,
        title: "",
        text: "",
        image: null,
        link: null,
        content: null,
    });
    const [newsItem, setNewsItem] = useState({});
    const [appSecurityLogIn, setAppSecurityLogIn] = useState(false);
    const [featureFilm, setFeatureFilm] = useState(null);
    const [isLoadingFeatureFilms, setIsLoadingFeatureFilms] = useState(false);
    const [isLoadingTopBanner, setIsLoadingTopBanner] = useState(false);
    const { children } = props;



    useEffect(() => {
        if (!subscriberGroupId) return;

        setIsLoadingFeatureFilms(true);
        (async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/media/feature-films/${subscriberGroupId}`);
                const filmList = res.data?.data?.feature_films || [];

                filmList.forEach(film => {
                    film.feature_url = film.feature_film_flag
                        ? `${film.ad_category_url}/${film.bo_movie_id}/${toSeoUrl(film.bo_movie_title)}`
                        : film.ad_category_url;

                    film.trailer_url = film.trailer_available ? film.trailer_url : "";
                });


                setFeatureFilm(filmList);
            } catch (err) {
                console.error("Failed to fetch feature films", err);
            } finally {
                setIsLoadingFeatureFilms(false);
            }
        })();
    }, [subscriberGroupId]);


    useEffect(() => {
        if (!subscriberGroupId || !siteVersion || siteVersion === SITE_V3) return;

        setIsLoadingTopBanner(true);
        (async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/media/top-banner/${subscriberGroupId}`);
                const topBannerList = res.data?.data?.top_banners || [];

                let selected = null;
                if (topBannerList.length) {
                    const rand = Math.floor(Math.random() * topBannerList.length);
                    selected = topBannerList[rand];
                    selected.feature_url = selected.feature_film_flag
                        ? `${selected.ad_category_url}/${selected.bo_movie_id}/${toSeoUrl(selected.bo_movie_title)}`
                        : selected.ad_category_url;
                }

                setTopBanner(selected);
            } catch (err) {
                console.error("Failed to fetch top banner", err);
            } finally {
                setIsLoadingTopBanner(false);
            }
        })();
    }, [subscriberGroupId, siteVersion]);



    const fetchSubscriberGroup = (groupCode) => {
        setIsLoading(true);
        axios
            .get(`${API_BASE_URL}/subscriber-group/${groupCode}`)
            .then(function(response) {
                setSubscriberGroup(response.data.data);
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error);
            });
    };


    let hasFetchedCinemas = false;

    const fetchSubscriberCinemas = () => {
        if (hasFetchedCinemas || cinemas.length > 0) return;
        hasFetchedCinemas = true;

        setIsLoading(true);
        axios
            .get(`${API_BASE_URL}/venue/get-subscriber-cinemas`)
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data.data)) {
                    setCinemas(response.data.data);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setIsLoading(false);
            });
    };


    const fetchWhiteLabel = (subscriberGroupId) => {
        setIsLoading(true);
        axios
            .get(`${API_BASE_URL}/subscriber-group-white-label/${subscriberGroupId}`)
            .then(function(response) {
                setIsLoading(false);
                const whiteLabelData = response?.data?.data?.white_label;
                setWhiteLabel(whiteLabelData);
                setAppSecurityLogIn(true);
                if (
                    whiteLabelData?.subscriber_group?.authentication_type === AUTH_JWT ||
                    whiteLabelData?.subscriber_group?.authentication_type === AUTH_PARTNER_INTEGRATION
                ) {
                    setAppSecurityLogIn(false);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
            });
    };

    const fetchSeasonalRoutes = () => {
        setIsLoading(true);
        axios
            .get(`${API_BASE_URL}/seasonal-voucher-manager`)
            .then(function(response) {
                setIsLoading(false);
                setSeasonalRoutes(response?.data?.data?.seasonalRoutes);
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
            });
    };

    return (
        <ApplicationContext.Provider
            value={{
                brandCode,
                cdnUrl,
                cinemas,
                fetchSubscriberCinemas,
                fetchSubscriberGroup,
                gaCode,
                isLoading,
                movies,
                publicSubscriberGroup,
                setBrandCode,
                setCdnUrl,
                setCinemas,
                setGaCode,
                brandName,
                setBrandName,
                setMovies,
                setPublicSubscriberGroup,
                setShowCarousel,
                setShowWelcomePopup,
                setSiteConfig,
                setSubscriberGroupId,
                setSubscriberGroupTerms,
                showCarousel,
                showWelcomePopup,
                siteConfig,
                subscriberGroup,
                subscriberGroupId,
                subscriberGroupTerms,
                templateTheme,
                whiteLabel,
                fetchWhiteLabel,
                productTypeId,
                setProductTypeId,
                fetchSeasonalRoutes,
                seasonalRoutes,
                topBanner,
                setTopBanner,
                news,
                setNews,
                newsItem,
                setNewsItem,
                appSecurityLogIn,
                featureFilm,
                setFeatureFilm,
                isLoadingTopBanner,
                isLoadingFeatureFilms
            }}
        >
            {children}
        </ApplicationContext.Provider>
    );
};

export default ApplicationContextProvider;
